// context/ChatbotContext.jsx
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ChatbotContext = createContext();

const initialMessages = [
  {
    id: 1,
    text: "Hello! I'm Q-Bot, your AI assistant from QUAVRION. How can I help you today?",
    sender: 'bot',
    timestamp: new Date(),
    type: 'greeting'
  }
];

const mockQuickResponses = [
  "Tell me about your services",
  "How can I contact the team?",
  "What positions are available?",
  "Do you offer consultations?"
];

export const ChatbotProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [quickResponses] = useState(mockQuickResponses);
  const [chatHistory, setChatHistory] = useState([]);
  
  const messagesEndRef = useRef(null);

  // Generate unique conversation ID
  useEffect(() => {
    if (!conversationId) {
      setConversationId(`conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    }
  }, [conversationId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      // Reset to initial state when opening
      setMessages(initialMessages);
    }
  }, [isOpen]);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: text.trim(),
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock responses based on keywords
      const responses = [
        "I understand you're interested in our services. Let me connect you with the right team.",
        "Based on your query, I recommend our Growth Reactor for marketing needs.",
        "For technical projects, our Digital Cortex team would be perfect. Shall I connect you?",
        "I'm processing your request and will provide a project scope shortly.",
        "Thank you for sharing your requirements. Our team will review this and get back to you."
      ];

      const responseText = responses[Math.floor(Math.random() * responses.length)];

      // Add bot response
      const botMessage = {
        id: Date.now() + 1,
        text: responseText,
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, botMessage]);

      // Update chat history
      setChatHistory(prev => [...prev, {
        user: text.trim(),
        bot: responseText,
        timestamp: new Date().toISOString(),
        conversationId
      }]);

    } catch (error) {
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm having trouble connecting right now. Please try again in a moment or contact us directly at contact@quavrion.com",
        sender: 'bot',
        timestamp: new Date(),
        type: 'error'
      };

      setMessages(prev => [...prev, errorMessage]);
      console.error('Chatbot error:', error);
    } finally {
      setIsTyping(false);
    }
  }, [conversationId]);

  const sendQuickResponse = useCallback((text) => {
    sendMessage(text);
  }, [sendMessage]);

  const clearChat = useCallback(() => {
    setMessages(initialMessages);
    setChatHistory([]);
  }, []);

  const exportChat = useCallback(() => {
    const chatData = {
      conversationId,
      timestamp: new Date().toISOString(),
      messages: messages.slice(1), // Skip initial greeting
      summary: `Chat with Q-Bot - ${messages.length - 1} messages`
    };

    const blob = new Blob([JSON.stringify(chatData, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quavrion-chat-${conversationId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [messages, conversationId]);

  const getConversationSummary = useCallback(() => {
    const userMessages = messages.filter(msg => msg.sender === 'user');
    const botMessages = messages.filter(msg => msg.sender === 'bot');
    
    return {
      totalMessages: messages.length - 1, // Exclude initial greeting
      userMessages: userMessages.length,
      botMessages: botMessages.length,
      lastMessage: messages[messages.length - 1]?.text || 'No messages',
      duration: messages.length > 1 
        ? `${Math.round((messages[messages.length - 1].timestamp - messages[1].timestamp) / 1000)}s`
        : '0s'
    };
  }, [messages]);

  const value = {
    isOpen,
    messages,
    isTyping,
    quickResponses,
    conversationId,
    chatHistory,
    toggleChat,
    openChat,
    closeChat,
    sendMessage,
    sendQuickResponse,
    clearChat,
    exportChat,
    getConversationSummary,
    messagesEndRef
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};